/** Persönliche Planung: Fristen, belegte Zeit und Zeitnachweise bleiben getrennt. */
export type PlanProvider = 'google' | 'microsoft' | 'caldav' | 'ics';
export type PlanDisplay = 'details' | 'busy' | 'hidden';
export type PlanHealth =
  | 'connecting'
  | 'syncing'
  | 'ready'
  | 'stale'
  | 'paused'
  | 'reauth'
  | 'error';
export type PlanBlockState = 'planned' | 'performed' | 'skipped' | 'inactive';
export interface PlanWindow {
  start: string;
  end: string;
}
export interface PlanPrefs {
  zone: string;
  confirmed: boolean;
  windows: Record<string, PlanWindow[]>;
  overrides: Record<string, PlanWindow[]>;
  minGap: number;
  bufferBefore: number;
  bufferAfter: number;
  version: number;
}
export interface PlanCandidate {
  id: string;
  title: string;
  contextId: string;
  context: string;
  due: string | null;
  estimate: number | null;
  href: string;
  active: boolean;
}
export interface PlanBlock {
  id: string;
  taskId: string | null;
  title: string;
  kind: 'task' | 'focus' | 'break' | 'travel' | 'other';
  start: string;
  end: string;
  zone: string;
  state: PlanBlockState;
  reason: string | null;
  version: number;
  context?: string;
  href?: string;
}
export interface PlanOccurrence {
  key: string;
  uid: string | null;
  recurrenceKey: string | null;
  title: string;
  start: string | null;
  end: string | null;
  startDay: string | null;
  endDay: string | null;
  busy: boolean;
  cancelled: boolean;
  declined: boolean;
  location: string | null;
  url: string | null;
  zone: string;
  incomplete?: boolean;
}
export interface PlanSource {
  id: string;
  connectionId: string;
  name: string;
  color: string;
  zone: string;
  enabled: boolean;
  display: PlanDisplay;
  blocksTime: boolean;
  allDayPolicy: 'provider' | 'hint' | 'busy';
  useBuffer: boolean;
  health: PlanHealth;
  checkedAt: string | null;
  error: string | null;
  version: number;
}
export interface PlanConnection {
  id: string;
  provider: PlanProvider;
  name: string;
  account: string;
  paused: boolean;
  version: number;
  sources: PlanSource[];
}
export interface PlanItem {
  id: string;
  kind: 'block' | 'event' | 'shift' | 'external' | 'taskDate';
  taskDate?: 'start' | 'due';
  title: string;
  start: string | null;
  end: string | null;
  startDay: string | null;
  endDay: string | null;
  busy: boolean;
  hidden: boolean;
  context: string;
  sourceId?: string;
  color: string;
  href?: string;
  location?: string;
  block?: PlanBlock;
}
/** Nur die persönliche Arbeitsplanung der anfragenden Person. */
export interface PlanTask {
  task: PlanCandidate;
  zone: string;
  blocks: PlanBlock[];
  hasMore: boolean;
}
export interface PlanInterval {
  start: number;
  end: number;
}
export interface PlanGap {
  start: string;
  end: string;
  minutes: number;
}
export interface PlanDay {
  day: string;
  gaps: PlanGap[];
  workMinutes: number;
  busyMinutes: number;
  freeMinutes: number;
  availability: 'confirmed' | 'incomplete' | 'notConfigured';
}
export interface PlanTimeline {
  from: string;
  to: string;
  zone: string;
  revision: string;
  prefs: PlanPrefs;
  items: PlanItem[];
  days: PlanDay[];
  candidates: PlanCandidate[];
  connections: PlanConnection[];
  warnings: string[];
  oauthRedirectUri?: string;
  providers: { provider: PlanProvider; enabled: boolean; defaultConfigured?: boolean }[];
}

export const planDayValid = (s: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(s) &&
  Number(s.slice(0, 4)) >= 1900 &&
  Number(s.slice(0, 4)) <= 2999 &&
  !Number.isNaN(Date.parse(s)) &&
  new Date(s).toISOString().slice(0, 10) === s;
export function planZoneValid(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return zone.length <= 80;
  } catch {
    return false;
  }
}
export const planAddDays = (day: string, n: number) =>
  new Date(Date.parse(day + 'T12:00:00Z') + n * 86400000)
    .toISOString()
    .slice(0, 10);
const zoneFormats = new Map<string, Intl.DateTimeFormat>();
export function planLocal(
  at: number | string | Date,
  zone: string,
): { day: string; time: string } {
  let f = zoneFormats.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    zoneFormats.set(zone, f);
  }
  const parts = Object.fromEntries(
    f.formatToParts(new Date(at)).map((p) => [p.type, p.value]),
  );
  return {
    day: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}
/** Liefert 0, 1 oder 2 Zeitpunkte. Lücken und doppelte Uhrzeiten nie still auflösen. */
export function planZonedTimes(
  day: string,
  time: string,
  zone: string,
): string[] {
  if (
    !planDayValid(day) ||
    !/^([01]\d|2[0-3]):[0-5]\d$/.test(time) ||
    !planZoneValid(zone)
  )
    return [];
  const nominal = Date.parse(`${day}T${time}:00Z`);
  const offsets = new Set<number>();
  for (let h = -36; h <= 36; h += 6) {
    const sample = nominal + h * 3600000;
    const local = planLocal(sample, zone);
    offsets.add(Date.parse(`${local.day}T${local.time}:00Z`) - sample);
  }
  return [...offsets]
    .map((o) => nominal - o)
    .filter((t) => {
      const l = planLocal(t, zone);
      return l.day === day && l.time === time;
    })
    .sort((a, b) => a - b)
    .map((t) => new Date(t).toISOString());
}
export function planMidnight(day: string, zone: string): number {
  // Auch Zonen mit einer Umstellung um Mitternacht: erste gültige Minute dieses Tages.
  for (let m = 0; m < 1440; m++) {
    const t = planZonedTimes(
      day,
      `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`,
      zone,
    )[0];
    if (t) return Date.parse(t);
  }
  throw new Error('Der Kalendertag existiert in dieser Zeitzone nicht.');
}
export function defaultPlanPrefs(zone = 'Europe/Berlin'): PlanPrefs {
  return {
    zone,
    confirmed: false,
    windows: Object.fromEntries(
      [0, 1, 2, 3, 4, 5, 6].map((d) => [
        String(d),
        d === 0 || d === 6 ? [] : [{ start: '09:00', end: '17:00' }],
      ]),
    ),
    overrides: {},
    minGap: 15,
    bufferBefore: 0,
    bufferAfter: 0,
    version: 0,
  };
}
export function mergePlanIntervals(intervals: PlanInterval[]): PlanInterval[] {
  const out: PlanInterval[] = [];
  for (const i of intervals
    .filter(
      (x) =>
        Number.isFinite(x.start) && Number.isFinite(x.end) && x.end > x.start,
    )
    .sort((a, b) => a.start - b.start || a.end - b.end)) {
    const last = out.at(-1);
    if (last && i.start <= last.end) last.end = Math.max(last.end, i.end);
    else out.push({ ...i });
  }
  return out;
}
export function subtractPlanIntervals(
  work: PlanInterval[],
  busy: PlanInterval[],
): PlanInterval[] {
  const occupied = mergePlanIntervals(busy);
  const out: PlanInterval[] = [];
  for (const w of mergePlanIntervals(work)) {
    let cursor = w.start;
    for (const b of occupied) {
      if (b.end <= cursor || b.start >= w.end) continue;
      if (b.start > cursor)
        out.push({ start: cursor, end: Math.min(b.start, w.end) });
      cursor = Math.max(cursor, b.end);
      if (cursor >= w.end) break;
    }
    if (cursor < w.end) out.push({ start: cursor, end: w.end });
  }
  return out;
}
export function planWorkWindows(day: string, prefs: PlanPrefs): PlanInterval[] {
  const result: PlanInterval[] = [];
  const bounds = {
    start: planMidnight(day, prefs.zone),
    end: planMidnight(planAddDays(day, 1), prefs.zone),
  };
  for (const d of [planAddDays(day, -1), day]) {
    const windows =
      prefs.overrides[d] ??
      prefs.windows[String(new Date(d + 'T12:00:00Z').getUTCDay())] ??
      [];
    for (const w of windows) {
      const start = planZonedTimes(d, w.start, prefs.zone)[0];
      const end = planZonedTimes(
        w.end <= w.start ? planAddDays(d, 1) : d,
        w.end,
        prefs.zone,
      ).at(-1);
      if (!start || !end) continue;
      const i = {
        start: Math.max(bounds.start, Date.parse(start)),
        end: Math.min(bounds.end, Date.parse(end)),
      };
      if (i.end > i.start) result.push(i);
    }
  }
  return mergePlanIntervals(result);
}
export function planItemInterval(
  item: PlanItem,
  zone: string,
): PlanInterval | null {
  if (item.start && item.end)
    return { start: Date.parse(item.start), end: Date.parse(item.end) };
  if (item.startDay && item.endDay)
    return {
      start: planMidnight(item.startDay, zone),
      end: planMidnight(item.endDay, zone),
    };
  return null;
}
export function calculatePlanDay(
  day: string,
  prefs: PlanPrefs,
  items: PlanItem[],
  complete: boolean,
  now = Date.now(),
  noBufferSources = new Set<string>(),
): PlanDay {
  const work = planWorkWindows(day, prefs);
  const busy: PlanInterval[] = [];
  for (const item of items) {
    if (!item.busy) continue;
    const i = planItemInterval(item, prefs.zone);
    if (!i) continue;
    const buffer =
      item.kind !== 'block' && !noBufferSources.has(item.sourceId ?? '');
    busy.push({
      start: i.start - (buffer ? prefs.bufferBefore * 60000 : 0),
      end: i.end + (buffer ? prefs.bufferAfter * 60000 : 0),
    });
  }
  const free = subtractPlanIntervals(work, busy);
  const duration = (v: PlanInterval[]) =>
    v.reduce((sum, i) => sum + (i.end - i.start) / 60000, 0);
  const today = planLocal(now, prefs.zone).day;
  const remaining = free.map((i) => ({
    ...i,
    start:
      day === today
        ? Math.max(i.start, Math.ceil(now / 300000) * 300000)
        : i.start,
  }));
  const availability = !prefs.confirmed
    ? 'notConfigured'
    : complete
      ? 'confirmed'
      : 'incomplete';
  return {
    day,
    availability,
    workMinutes: duration(work),
    freeMinutes: duration(free),
    busyMinutes: duration(work) - duration(free),
    gaps:
      availability !== 'confirmed' || day < today
        ? []
        : remaining
            .filter((i) => (i.end - i.start) / 60000 >= prefs.minGap)
            .map((i) => ({
              start: new Date(i.start).toISOString(),
              end: new Date(i.end).toISOString(),
              minutes: (i.end - i.start) / 60000,
            })),
  };
}
export function planConflicts(
  start: string,
  end: string,
  items: PlanItem[],
  excludeId?: string,
): PlanItem[] {
  const a = Date.parse(start),
    b = Date.parse(end);
  return items.filter(
    (i) =>
      i.id !== excludeId &&
      i.busy &&
      i.start &&
      i.end &&
      Date.parse(i.start) < b &&
      Date.parse(i.end) > a,
  );
}
